import { atom, useAtom } from 'jotai';
import { CompleteAccountModalContext } from '@/redux/modules/modal';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { generateBidOptions, getNextBidMinimum, MAX_BID_LIMIT, roundAbsenteeBid } from '@/utils/absenteeBids';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { getBidLimitBalance, getTotalBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import {
    getItemModelCatalogId,
    getItemModelCurrency,
    getItemModelIsTimedPlusAuction,
    getItemModelStartPrice,
    getItemSummaryBidderMaxBid,
    getItemSummaryLeadingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { isItemPage } from '@/utils/urls';
import { OptionType } from '@liveauctioneers/hammer-ui-core/select';
import { SelectCreatable } from '@liveauctioneers/hammer-ui-core/selectCreatable';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import Input from './components/Input';
import MenuList from './components/Menulist';
import useGetModalContextInfo from '@/components/Modals/AuthModal/hooks/useGetModalContextInfo';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

export const temporaryBidderMaxBidAtom = atom(0);

export type SelectBidDropdownProps = {
    disabled?: boolean;
    itemId: number;
    onChangeBid?: (bidAmount: number, overBidLimit: boolean, bidWasRounded: boolean) => void;
    showPlaceholderOnInit?: boolean;
} & Partial<ComponentProps<typeof SelectCreatable>>;

interface SelectOption extends Omit<OptionType, 'label'> {
    isDisabled?: boolean;
    label: any;
}

const SelectBidDropdown = ({
    disabled,
    itemId,
    onChangeBid,
    showPlaceholderOnInit = false,
    ...selectCreatableProps
}: SelectBidDropdownProps) => {
    const { pathname } = useLocation();
    const isOnItemPage = isItemPage(pathname);
    const { formatMessage } = useIntl();
    const [oldValue, setOldValue] = useState(null);
    const [shouldShowPlaceholder, setShouldShowPlaceholder] = useState(showPlaceholderOnInit);
    const bidderId = useAppSelector(getBidderId);
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId)) || 0;
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemModelCurrency(state, itemId)) || 'USD';
    const isTimedPlusAuction = useAppSelector((state) => getItemModelIsTimedPlusAuction(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId)) || 0;
    const startPrice = useAppSelector((state) => getItemModelStartPrice(state, itemId)) || 0;
    const [temporaryBidderMaxBid, setTemporaryBidderMaxBid] = useAtom(temporaryBidderMaxBidAtom);

    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));

    const totalCatalogBidLimitBalance = useAppSelector((state) => getTotalBidLimitBalance(state, catalogId));
    const getPlaceholderText = () => {
        if (isTimedPlusAuction && !showPlaceholderOnInit) {
            return formatMessage({ id: 'select_create_bid_timed_plus_placeholder' });
        }
        if (showPlaceholderOnInit) {
            return formatMessage({ id: 'select_create_bid_placeholder_experiment' });
        }
        return formatMessage({ id: 'select_create_bid_placeholder' });
    };

    const { itemId: modalItemId, type: modalType } = useGetModalContextInfo();
    const previousModalType = usePrevious(modalType);

    const formatCreateLabel = useCallback(
        (inputValue: string) => (
            <FormattedMessage
                id="select_create_bid_option"
                values={{
                    amount: (
                        <span key="select-create-bid-option-container">
                            &nbsp;
                            <FormattedCurrency
                                convert
                                key="select-create-bid-option"
                                sourceCurrency={currency}
                                value={Number(inputValue)}
                            />
                        </span>
                    ),
                }}
            />
        ),
        [currency]
    );

    // // need to be sure to set the bidAmount initially
    const [bidAmount, setBidAmount] = useState(
        temporaryBidderMaxBid > 0
            ? temporaryBidderMaxBid
            : getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );
    const [stateItemId, setItemId] = useState(itemId || 0);
    const [newBidMinimum, setNewBidMinimum] = useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    /**
     * Creates list of bidding options for Select dropdown
     */
    const bidSelectOptions = useMemo((): SelectOption[] => {
        const newBidIncrements = generateBidOptions(bidIncrements, Math.max(bidAmount, newBidMinimum), newBidMinimum);

        return newBidIncrements.map((bid) => ({
            // Disable option if it exceeds user's bid limit
            isDisabled: bid > bidLimitBalance,
            label: (
                <FormattedCurrency
                    convert
                    key="select-bid-option"
                    noFraction
                    sourceCurrency={currency}
                    value={bid}
                />
            ),
            value: bid,
        }));
    }, [bidIncrements, bidAmount, newBidMinimum, bidLimitBalance, currency]);

    // Initialize selectValueRef based on experiment flag
    const selectValueRef = useRef(
        shouldShowPlaceholder ? null : bidSelectOptions.find((option) => option.value === bidAmount)
    );

    const isValidNewOption = useCallback(
        (newBidLimit: string) => {
            const inputNumber = Number(newBidLimit);
            const isNewOption = !Boolean(bidSelectOptions.find((option) => option.value === inputNumber));
            return (
                Boolean(inputNumber) &&
                isNewOption &&
                inputNumber >= newBidMinimum &&
                inputNumber <= MAX_BID_LIMIT &&
                inputNumber <= bidLimitBalance
            );
        },
        [bidSelectOptions, newBidMinimum, bidLimitBalance]
    );

    const onChange = useCallback(
        (newValue: SelectOption) => {
            const originalValue = newValue.value;
            newValue.value = roundAbsenteeBid(bidIncrements, Number(newValue.value));
            newValue.label = (
                <FormattedCurrency
                    convert
                    key="select-bid-option"
                    noFraction
                    sourceCurrency={currency}
                    value={newValue.value}
                />
            );

            const bidWasRounded = originalValue !== newValue.value;
            const overBidLimit = newValue.value > bidLimitBalance;

            // update select value and bid options
            selectValueRef.current = newValue;
            setShouldShowPlaceholder(false); // User has made a selection, no longer show placeholder

            setOldValue(null);
            setIsMenuOpen(false);

            const bidAmount = newValue.value;
            setBidAmount(bidAmount);
            setTemporaryBidderMaxBid(bidAmount);
            if (onChangeBid) {
                onChangeBid(bidAmount, overBidLimit, bidWasRounded);
            }
        },
        [bidIncrements, bidLimitBalance, currency, onChangeBid, selectValueRef, setBidAmount, setTemporaryBidderMaxBid]
    );

    const onBlur = useCallback(() => {
        setIsMenuOpen(false);

        // if no option or input value is selected, revert to the previous select value
        // onInputChange updates are handled here too

        // no update if select value is not null
        if (selectValueRef.current !== null) {
            return;
        }

        // no update if oldvalue is null
        if (oldValue === null) {
            return;
        }

        // no update if oldValue is < minimum bid
        if (oldValue.value < newBidMinimum) {
            return;
        }

        // Validate that the entered value doesn't exceed limits (matches isValidNewOption logic)
        // bidLimitBalance: user's personal spending limit (the primary protection)
        // MAX_BID_LIMIT: system-wide maximum to prevent database/system issues
        if (oldValue.value > bidLimitBalance || oldValue.value > MAX_BID_LIMIT) {
            return;
        }

        // set the new select value and update bid options
        selectValueRef.current = oldValue;
        setShouldShowPlaceholder(false);
        onChange(oldValue);
        setOldValue(null);

        const bidAmount = Number(oldValue.value);
        setBidAmount(bidAmount);
    }, [bidLimitBalance, newBidMinimum, oldValue, onChange, setBidAmount, setOldValue]);

    const previousBidLimitBalance = usePrevious(bidLimitBalance);
    const previousItemId = usePrevious(stateItemId);
    const previousNewBidMinimum = usePrevious(newBidMinimum);

    // If the bid (or its environment) changes, update state values
    useEffect(() => {
        const balance = bidLimitBalance;

        // We need to store the lowest bid option so we can update the state when
        // the bid options change
        const minimumBid = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

        const hasBidParametersChanged =
            previousBidLimitBalance !== balance ||
            previousItemId !== itemId ||
            previousNewBidMinimum !== minimumBid ||
            bidAmount <= minimumBid; // if the bid amount is less than the minimum bid that means we've been outbid and we need to update the bid amount

        if (hasBidParametersChanged) {
            const newBidAmount = temporaryBidderMaxBid > 0 ? temporaryBidderMaxBid : minimumBid;

            setBidAmount(newBidAmount);
            setItemId(itemId);
            setNewBidMinimum(minimumBid);

            // Ensure that selectValueRef.current is updated to the new bid amount when changed externally
            // Only update if we're not showing placeholder or if user has made a selection
            if (!shouldShowPlaceholder && selectValueRef.current?.value !== newBidAmount) {
                let newValue = bidSelectOptions.find((option) => option.value === newBidAmount);
                if (!newValue && temporaryBidderMaxBid > 0) {
                    // Create a new option if temporaryBidderMaxBid is not in the list
                    newValue = {
                        isDisabled: temporaryBidderMaxBid > bidLimitBalance,
                        label: (
                            <FormattedCurrency
                                convert
                                key="select-bid-option"
                                noFraction
                                sourceCurrency={currency}
                                value={temporaryBidderMaxBid}
                            />
                        ),
                        value: temporaryBidderMaxBid,
                    };
                }
                if (newValue) {
                    selectValueRef.current = newValue;
                }
            }

            const overBidLimit = newBidAmount > balance;
            if (onChangeBid) {
                onChangeBid(newBidAmount, overBidLimit, false);
            }
        }
    }, [
        bidIncrements,
        bidLimitBalance,
        bidderMaxBid,
        stateItemId,
        leadingBid,
        previousBidLimitBalance,
        previousItemId,
        previousNewBidMinimum,
        startPrice,
        itemId,
        bidderId,
        bidAmount,
        totalCatalogBidLimitBalance,
        onChangeBid,
        bidSelectOptions,
        currency,
        temporaryBidderMaxBid,
        shouldShowPlaceholder,
    ]);

    useEffect(() => {
        // if the itemId changes then we need to reset the select value.
        // we also need to reset the select value if the bid amount changes externally by adding bidAmount to the dependency array
        if (previousItemId !== itemId) {
            // Reset to show placeholder if experiment is enabled and user hasn't made a selection
            if (showPlaceholderOnInit && shouldShowPlaceholder) {
                selectValueRef.current = null;
            } else {
                selectValueRef.current = bidSelectOptions.find((option) => option.value === bidAmount);
            }
        }
    }, [bidAmount, itemId, bidSelectOptions, previousItemId, showPlaceholderOnInit, shouldShowPlaceholder]);

    const onInputChange = useCallback(
        (input: string) => {
            // if the input is empty do not update old value for blur update
            if (input === '') {
                return;
            }

            // if the input is less than min bid do not update old value for blur update
            if (Number(input) < newBidMinimum) {
                return;
            }

            selectValueRef.current = null;

            // update old value on input put so if user blurs out the bid is updated
            const value = Number(input);

            const label = (
                <FormattedCurrency
                    convert
                    key="select-bid-option"
                    noFraction
                    sourceCurrency={currency}
                    value={value}
                />
            );
            const newOption: SelectOption = {
                isDisabled: false,
                label,
                value,
            };
            setOldValue(newOption);
        },
        [currency, newBidMinimum]
    );

    useEffect(() => {
        // Only run if itemId actually changed from a previous value
        if (
            (previousItemId !== undefined && previousItemId !== itemId) ||
            // If modal closes, we want the BidCanvas to reset to show the placeholder
            (previousModalType === CompleteAccountModalContext.PlaceBid && !Boolean(modalItemId))
        ) {
            setTemporaryBidderMaxBid(0);
            selectValueRef.current = null;
            setShouldShowPlaceholder(showPlaceholderOnInit);
        }

        // Fix: LAAC-811
        // When the bid modal is opened outside item page the bidAmount is kept to the previous value so we need to reset the temporaryBidderMaxBid
        // This fixes the issue where a user can select a bid amount on 'My Items' page and then opens the Bid Modal for a higher start price item
        return () => {
            if (!isOnItemPage) {
                setTemporaryBidderMaxBid(0);
                selectValueRef.current = null;
            }
        };
    }, [
        itemId,
        previousItemId,
        setTemporaryBidderMaxBid,
        isOnItemPage,
        previousModalType,
        modalItemId,
        showPlaceholderOnInit,
    ]);

    return (
        <div className="flex flex-col">
            <SelectCreatable
                components={{
                    Input,
                    MenuList: (props) => (
                        <MenuList
                            {...props}
                            onCreateOption={() => {
                                setOldValue(selectValueRef.current);
                                setIsMenuOpen(false);
                                selectValueRef.current = null;
                            }}
                        />
                    ),
                }}
                createOptionPosition="first"
                data-testid="place-bid-dropdown"
                disabled={disabled}
                formatCreateLabel={formatCreateLabel}
                isValidNewOption={isValidNewOption}
                menuIsOpen={isMenuOpen}
                onBlur={onBlur}
                onChange={onChange}
                onInputChange={onInputChange}
                onMenuOpen={() => setIsMenuOpen(true)}
                options={bidSelectOptions}
                placeholder={getPlaceholderText()}
                value={selectValueRef.current}
                {...selectCreatableProps}
            />
        </div>
    );
};

export default SelectBidDropdown;
